import React, { Component, Fragment, useState, useEffect, useContext, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

// d.ts
import { ViewOptionsType } from "@/@types/common/index";
import { IMapProps } from "@/@types/views/GIS/map";

// OpenLayers
import Geometry from "ol/geom/Geometry";
import proj4, { Point } from "proj4";
import { register as OlRegister } from "ol/proj/proj4";
import { get as OlGetProjection } from "ol/proj";
import Projection from "ol/proj/Projection";
import OlLayerGroup from "ol/layer/Group";
import OlSourceVector from "ol/source/Vector";
import Map from "@/components/Map/Map";
import MapEvent from "ol/MapEvent";
import { DrawEvent } from "ol/interaction/Draw";
import BaseEvent from "ol/events/Event";
import OlMapBrowserEvent from "ol/MapBrowserEvent";
import OlBaseLayer from "ol/layer/Base";
import OlFeature from "ol/Feature";
import OlCollection from "ol/Collection";
import OlFormatWKT from "ol/format/WKT";
import * as OlExtent from "ol/extent";

// Custom
import { Controls, ZoomControl, ScaleLineControl } from "@/components/Map/Controls";

import { Layers, TileLayer, VectorLayer, GroupLayer } from "@/components/Map/Layers";
import { Overlays } from "@/components/Map/Overlays";
import { Interactions, DefaultInteractions, MouseWheelZoomInteraction, DrawInteraction } from "@/components/Map/Interactions";
import { selectedStyle, measuringStyle } from "@/components/Map/mapStyles";
import UserContext from "@/components/UserContext/UserContext";

import GeoBaseLayerSwitcher from "@/components/Map/Controls/GEO/GeoBaseLayerSwticher";
import GeoAPILayers from "@/components/Map/Layers/GEO/GeoAPILayers";

//Services
import mapService from "@/services/mapService";

type FeatureMapMode = "insert" | "view" | "update"

type FeatureMapProps = {
  mapId?: number;
  wkt: string;
  proj?: string;
  mode: FeatureMapMode;
  geometryChangeType?: "LineString" | "Polygon" | "Point"
  onGeometryChange?: (wkt: string) => void;
};

function FeatureMap(props: FeatureMapProps) {
  const userContext = useContext(UserContext);
  const { t } = useTranslation();

  const validProjs = ['EPSG:3857', 'EPSG:3765']

  const mapId = props.mapId ? props.mapId : 3;

  const geometryChangeType = !props.geometryChangeType ? "Point" : props.geometryChangeType

  const dataProj = (props.proj && validProjs.indexOf(props.proj) >= 0) ? props.proj : validProjs[0];

  const { wkt, mode, onGeometryChange } = props;

  const [zoomToExtent, setZoomToExtent] = useState<number[] | undefined>(undefined);

  const [mapInitialized, setMapInitialized] = useState(false);

  const [viewOptions, setViewOptions] = useState<ViewOptionsType>({
    center: userContext?.mapSettings ? userContext.mapSettings.initial_view_center : [1731757, 5581737],
    zoom: userContext?.mapSettings ? userContext.mapSettings.initial_view_zoom : 14,
    extent: userContext?.mapSettings ? userContext.mapSettings.max_extent : undefined,
    //projection: wgs84PM,
    minZoom: 4,
    maxZoom: 17,
    constrainResolution: true
  });

  const [layersCollection, setLayersCollection] = useState<OlCollection<OlBaseLayer> | undefined>(undefined);

  const [selectedSource, setSelectedSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));

  const [drawingSource, setDrawingSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));

  proj4.defs(
    "EPSG:3765",
    "+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
  );
  proj4.defs(
    "EPSG:3857",
    "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs"
  );
  OlRegister(proj4);

  useEffect(() => {
    getLayers();
  }, []);

  useEffect(() => {
    if (wkt) {
      const wktFormatter = new OlFormatWKT();
      try {
        const feat: OlFeature<Geometry> | null = wkt ? wktFormatter.readFeature(wkt, {
          dataProjection: dataProj,
          featureProjection: "EPSG:3857"
        }) : null;

        if (feat) {
          setSelectedSource(new OlSourceVector<OlFeature<Geometry>>({ features: [feat] }));

          const geom = feat.getGeometry();
          if (geom) {
            const extent = geom.getExtent();
            const center = OlExtent.getCenter(extent) as [number, number];
            setViewOptions(prevState => ({
              ...prevState,
              center: center
            }));
            setZoomToExtent(extent);
          }
        } else {
          setSelectedSource(new OlSourceVector<OlFeature<Geometry>>({ features: [] }));
        }
      } catch (error) {
        console.error("Invalid WKT:", error);
        setSelectedSource(new OlSourceVector<OlFeature<Geometry>>({ features: [] }));
        // Optionally set an error state or handle the error as needed
      }
    } else {
      setSelectedSource(new OlSourceVector<OlFeature<Geometry>>({ features: [] }));
    }
  }, [wkt, dataProj]);

  const getLayers = () => {
    mapService.getLayers(mapId).then((coll) => {
      setLayersCollection(coll);
      setMapInitialized(true);
    });
  };

  const handleDrawMeasureStart = useCallback((evt: DrawEvent) => {
  }, []);

  const handleDrawMeasureEnd = useCallback((evt: DrawEvent) => {
    if (evt.feature) {
      setSelectedSource(new OlSourceVector({ features: [evt.feature] }));

      if (onGeometryChange) {
        const wktFormatter = new OlFormatWKT();
        const wkt = wktFormatter.writeFeature(evt.feature, {
          dataProjection: "EPSG:3765",
          featureProjection: "EPSG:3857",
          decimals: 2
        } )
        onGeometryChange(wkt);
      }
    }
  }, [onGeometryChange]);

  const handleDrawMeasureChange = useCallback((evt: BaseEvent) => {
    // console.log("handleDrawChange", evt);
  }, []);

  // getDefaultData() {
  //   mapService.getDefaultData().then((data) => {
  //     if (data) {
  //       const viewData = Array.isArray(data) ? Object.assign({}, data[0]) : Object.assign({}, data);
  //       this.setState((prevState) => {
  //         return {
  //           ...prevState,
  //           viewOptions: {
  //             ...prevState.viewOptions,
  //             zoom: viewData.initial_view_zoom,
  //             center: viewData.initial_view_center
  //           },
  //           zoomToExtent: viewData.default_extent
  //         };
  //       });
  //     }
  //   });
  // }

  return (
    <Fragment>
      {userContext && layersCollection ? (
        <Map
          mapId={mapId}
          height="400px"
          view={viewOptions}
          className="feature-map"
          id="feature-map"
          initialized={mapInitialized}
          moveTolerance={10}
          maxTilesLoading={10}
          zoomToExtent={zoomToExtent}
          zoomToExtentPadding={[20, 20, 20, 20]}
        >
          <Controls>
            <ZoomControl zoomInTipLabel={t("map:controls.zoom_in")} zoomOutTipLabel={t("map:controls.zoom_out")} />
            <ScaleLineControl />
            <GeoBaseLayerSwitcher mapId={mapId} allowNoLayer={false} />
          </Controls>
          <Layers>
            {layersCollection ? (
              <Layers>
                <GeoAPILayers layersCollection={layersCollection} />
                <VectorLayer id="selected" source={selectedSource} style={selectedStyle} zIndex={800} />
              </Layers>
            ) : null}
          </Layers>
          <Overlays></Overlays>
          <Interactions>
            {mode === "insert" || mode === "update" ?
              <DefaultInteractions /> : null}
            {mode === "view" || mode === "update" ?
              <MouseWheelZoomInteraction useAnchor={false} /> : null}
            {mode === "insert" || mode === "update" ?
              <DrawInteraction
                source={drawingSource}
                type={geometryChangeType}
                style={measuringStyle}
                onChange={(handleDrawMeasureChange)}
                onDrawstart={handleDrawMeasureStart}
                onDrawend={handleDrawMeasureEnd}
              
              />
              : null}

          </Interactions>
        </Map>
      ) : null}
    </Fragment>
  );
}

export default FeatureMap;
